/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect} from 'react'

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ProductScroller from '../../components/product-scroller'

import CategoryGrid from '../../components/category-grid'
import HeroSection from '../../components/hero-section'
import IngredientGrid from './ingredient-grid'
import VideoSection from '../../components/video-section'
import ServiceCard from '../../components/home-service-section/service-card'
import Seo from '../../components/seo'
import {usePageQuery} from '../../clients/contentful/hooks/use-page-query.hook'

const Section = ({sectionData}) => {
    const sectionName = sectionData?.name ?? sectionData?.sectionName ?? ''

    switch (true) {
        case sectionName.startsWith('homePageHeroSection'):
            return (
                <HeroSection
                    data={sectionData}
                    marginX={{base: '17px', md: '0'}}
                />
            )
        case sectionName.startsWith('homeProductCarousel'):
            return (
                <ProductScroller
                    productCarouselContent={{
                        carouselCollection: {items: [sectionData]}
                    }}
                    isLoading={false}
                    px={{base: '16px', lg: '32px', xl: '43px'}}
                    showScrollerNav
                    maxWidth={{
                        base: 'calc(100vw - 40px)',
                        lg: 'calc(100vw - 32px)'
                    }}
                    itemWidth={{
                        base: 'calc(50% - 6.5px)',
                        md: 'calc(33.33% - 8.66px)',
                        lg: 'calc(25% - 9.75px)',
                        xl: 'calc(25% - 9.75px)'
                    }}
                    cardsGap="13px"
                    scrollContainerId="product-scroller-container"
                />
            )
        case sectionName.startsWith('homeContentGrid'):
            return <CategoryGrid sectionNameKey={sectionName} />
        case sectionName.startsWith('homeVideoSection'):
            return (
                <VideoSection
                    videoSectionData={sectionData}
                    borderRadius="20px"
                    py={{base: '20px', md: 'none'}}
                />
            )
        case sectionName.startsWith('homeBookingSection'):
            return (
                <>
                    {sectionData?.itemsCollection?.items?.map((item) => (
                        <ServiceCard serviceCard={item} variant="service" />
                    ))}
                </>
            )
        case sectionName.startsWith('homeIngredientGlossary'):
            return <IngredientGrid data={sectionData} />
        default:
            return <></>
    }
}

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */

const Home = () => {
    const {filteredQuery: pageContent} = usePageQuery('home')

    return (
        <Box
            data-testid="home-page"
            layerStyle="homePage"
            gap={{base: '30px', lg: '80px'}}
        >
            <Seo
                title="Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada"
                description="Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada"
                keywords="Rennaï, MountRoyal, Canada, Montreal"
            />
            {pageContent?.map((sectionData) => (
                <Section sectionData={sectionData} />
            ))}
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default Home
